<template>
  <div
    v-if="isLoading || scenePreviews.length > 0"
    class="bvs-scene-previews"
    data-testid="pdp-bvs-scene-previews"
  >
    <Spacer spacing-grid="xsmall" />
    <h2 class="bvs-scene-previews__title">
      {{
        $textDictionary['bvs.pdp.scenePreviews.title'] ??
        'Simulate your application'
      }}
    </h2>
    <ul class="product-detail-gallery__items bvs-scene-previews__items">
      <template v-if="scenePreviews.length > 0">
        <li
          v-for="scene in scenePreviews"
          :key="scene.id"
          class="bvs-scene-previews__item"
        >
          <a
            class="product-detail-gallery__item-btn"
            href="#pdp-bvs-simulation"
            :aria-label="scenePreviewLabel(scene.label)"
            :title="scene.label"
            @click.prevent="emit('select-scene', scene.id)"
          >
            <span class="bvs-scene-previews__media">
              <Media :media="scene.media" :cols="[4, 3, 2]" :lazyload="true" />
            </span>
            <span class="bvs-scene-previews__overlay">
              {{ scenePreviewCta(scene.label) }}
            </span>
          </a>
        </li>
      </template>
      <li
        v-for="placeholder in placeholderCount"
        v-else
        :key="`placeholder-${placeholder}`"
        class="bvs-scene-previews__item"
      >
        <LoadingSkeleton type="block" class="bvs-scene-previews__skeleton" />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import {
  LoadingSkeleton,
  Media,
  Spacer
} from '@hypercodestudio/basler-components';
import type { MediaItemInterface } from '@hypercodestudio/basler-components';
import type { BvsCameraManifest } from '~/types/bvs';
import { buildBvsScenePreviewMedia } from '~/utils/bvs/buildBvsScenePreviewMedia';

interface Props {
  manifest?: BvsCameraManifest;
  isLoading?: boolean;
}

interface Emits {
  (event: 'select-scene', sceneId: string): void;
}

interface ScenePreview {
  id: string;
  label: string;
  media: MediaItemInterface;
}

const SCENE_PREVIEW_LIMIT = 3;

const props = withDefaults(defineProps<Props>(), {
  manifest: undefined,
  isLoading: false
});
const emit = defineEmits<Emits>();
const { $textDictionary } = useNuxtApp();

const placeholderCount = SCENE_PREVIEW_LIMIT;

const scenePreviews = computed<ScenePreview[]>(() => {
  const manifest = props.manifest;
  if (!manifest) {
    return [];
  }
  return manifest.scenes
    .flatMap((scene) => {
      const media = buildBvsScenePreviewMedia(manifest, scene);
      return media ? [{ id: scene.id, label: scene.label, media }] : [];
    })
    .slice(0, SCENE_PREVIEW_LIMIT);
});

function scenePreviewLabel(sceneLabel: string): string {
  const template =
    $textDictionary.value['bvs.pdp.scenePreview.ariaLabel'] ??
    'Go to camera image simulation for {application}';
  return template.replace('{application}', sceneLabel);
}

function scenePreviewCta(sceneLabel: string): string {
  const template =
    $textDictionary.value['bvs.pdp.scenePreview.cta'] ??
    'Simulate {application}';
  return template.replace('{application}', sceneLabel);
}
</script>

<style scoped>
.bvs-scene-previews {
  width: 100%;
}

.bvs-scene-previews__title {
  margin: 0 0 var(--spacing-size-xsmall);
  color: var(--bslr-color-headline);
  font-size: 1.125rem;
  text-align: center;
  text-wrap: balance;
}

.bvs-scene-previews__items {
  width: 100%;
  align-content: start;
}

.bvs-scene-previews__item {
  flex: 0 0 calc(50% - 0.5rem);
  align-self: start;
  min-width: 0;
  border-radius: 12px;
  overflow: hidden;
}

.bvs-scene-previews__item .product-detail-gallery__item-btn {
  position: relative;
  overflow: hidden;
}

.bvs-scene-previews__media {
  display: block;
  transition: filter 200ms ease;
}

.bvs-scene-previews__overlay {
  display: grid;
  position: absolute;
  inset: 0;
  padding: var(--spacing-size-xsmall);
  place-items: center;
  color: #fff;
  font-weight: 600;
  text-align: center;
  text-wrap: balance;
  background: rgb(0 0 0 / 30%);
  opacity: 0;
  transition: opacity 200ms ease;
}

.bvs-scene-previews__item
  .product-detail-gallery__item-btn:is(:hover, :focus-visible)
  .bvs-scene-previews__media {
  filter: blur(3px);
}

.bvs-scene-previews__item
  .product-detail-gallery__item-btn:is(:hover, :focus-visible)
  .bvs-scene-previews__overlay {
  opacity: 1;
}

/* Matches the 1920x1200 renders so the tiles do not resize once they arrive. */
.bvs-scene-previews__skeleton {
  display: block;
  width: 100%;
  aspect-ratio: 8 / 5;
}
</style>
